import { type FC } from "react";
import { useSearchParams } from "react-router-dom";

import { Badge, Group, SegmentedControl, Stack } from "@mantine/core";
import { useIntl } from "react-intl";

import { InsightsNotPermittedPage } from "../../core/components";
import { NavigateWithSearch } from "../../core/routes/NavigateWithSearch";
import { PageContentSpinner, PageHeader } from "../../layout/components";
import { AllTime } from "../../lens-creator-payout/components/messages.generated";
import { SelectTimeRange } from "../../lens-insights/components/select-time-range";
import { ROLE_ACTIONS } from "../../shared/constants";
import { useSelectedOrganizationContainer } from "../../shared/hooks";
import { usePermittedBasedOnLensFolderRoleAction } from "../../shared/hooks/roles";
import { usePageRedirect } from "../../shared/hooks/usePageRedirect";
import { Feature } from "../../shared/models";
import { useMonetizationSelectedTab } from "../hooks";
import { useLensPlusMonth } from "../hooks/useLensPlusMonth";
import { useLensPlusPayoutHistory } from "../hooks/useLensPlusPayoutHistory";
import { MonetizationTab } from "../models";
import { resolveMonetizationRedirect } from "../utils/monetizationNavigation";

import { MonetizationTabs } from "./MonetizationTabs";
import { page } from "./MonetizationTabs.module.css";
import { messages as lensPlusMessages } from "./lens-plus/messages.generated";
import { PageTitle } from "./messages.generated";

export const MonetizationPage: FC = () => {
    const selection = useLensPlusMonth();
    const { month, options, setMonth, view, setView } = selection;
    const intl = useIntl();
    const redirect = usePageRedirect(Feature.monetization);
    const { tab, canonicalRedirectPath, navigateToTab } = useMonetizationSelectedTab();
    const { lensFolderId } = useSelectedOrganizationContainer();
    const { isPermitted, loading: isPermittedLoading } = usePermittedBasedOnLensFolderRoleAction(
        lensFolderId ?? "",
        ROLE_ACTIONS.READ_LENS_INSIGHTS,
    );

    const [params] = useSearchParams();
    const history = useLensPlusPayoutHistory(
        month,
        params.get("lensId"),
        view === "all-time",
        tab !== MonetizationTab.LensPlus ||
            isPermittedLoading ||
            Boolean(lensFolderId && !isPermitted) ||
            Boolean(redirect ?? canonicalRedirectPath),
    );
    const pickerOptions = options.map((option) => {
        const paid = history.paidPoints.find((point) => point.month === option.value);
        const status = paid ? "paid" : option.value === new Date().toISOString().slice(0, 7) ? "pending" : undefined;
        return {
            ...option,
            // A missing history record does not establish an unpaid or unavailable month.
            detail: status ? (
                <Badge radius="xl" tt="none" color={status === "paid" ? "green" : "orange"}>
                    {intl.formatMessage(lensPlusMessages.statusLabel, { status })}
                </Badge>
            ) : undefined,
        };
    });

    if (redirect) {
        return <NavigateWithSearch to={resolveMonetizationRedirect(redirect, tab)} replace />;
    }

    if (canonicalRedirectPath) {
        return <NavigateWithSearch to={canonicalRedirectPath} replace />;
    }

    if (isPermittedLoading) {
        return <PageContentSpinner />;
    }

    if (lensFolderId && !isPermitted) {
        return <InsightsNotPermittedPage />;
    }

    return (
        <Stack gap="lg" pb="lg" className={page}>
            <Group justify="space-between">
                <PageHeader>
                    <PageTitle />
                </PageHeader>
                {tab === MonetizationTab.LensPlus && (
                    <Group gap="sm">
                        <SegmentedControl
                            value={view === "all-time" ? "all-time" : "monthly"}
                            radius="xl"
                            withItemsBorders={false}
                            data={[
                                { value: "monthly", label: intl.formatMessage(lensPlusMessages.monthly) },
                                { value: "all-time", label: <AllTime /> },
                            ]}
                            onChange={(mode) => {
                                if (mode === "monthly" || mode === "all-time") setView(mode);
                            }}
                        />
                        {view !== "all-time" && (
                            <SelectTimeRange
                                selection={{
                                    options: pickerOptions,
                                    value: month,
                                    onChange: setMonth,
                                }}
                            />
                        )}
                    </Group>
                )}
            </Group>
            <MonetizationTabs tab={tab} onChange={navigateToTab} selection={selection} history={history} />
        </Stack>
    );
};
